import React, { CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { AgentTaskOptions } from "api/maestroTasksApi";
import useScreen from 'Hooks/useScreen';
import Colors from 'modules/Colors';
import { styx } from 'modules/Utils';
import Icon from 'elements/Icon';
import useMaestroTasks, { TaskType } from 'Hooks/useMaestroTasks';
import Loader from 'elements/Loader';
import { useAuth0 } from '@auth0/auth0-react';

interface DailyTasksProps {
  tasks?: AgentTaskOptions[]; // Make this optional since we're using predefined tasks
  onTaskClick: (task: AgentTaskOptions) => void;
}

const DailyTasks: React.FC<DailyTasksProps> = ({
  tasks,
  onTaskClick,
}) => {
  const { mediumView, mStyle } = useScreen();
  const { isAuthenticated } = useAuth0();
  const { recommendedTasks, isLoadingRecommendedTasks } = useMaestroTasks();
  // Choose which tasks to show based on MOCK flag
  let tasksToShow: AgentTaskOptions[] = [];

  if (isAuthenticated) {
    // Use API tasks if loaded, otherwise fall back to predefined tasks
    tasksToShow = recommendedTasks
  } else {
    tasksToShow = [
      {
        id: '1',
        type: TaskType.CAMPAIGN,
        title: 'Promote your latest release',
        prompt: 'Promote my latest release',
        description: 'Share your new music with the world.',
        priority: 'high',
        estimatedTime: '30 minutes',
        relatedContent: null
      },
      {
        id: '2',
        type: TaskType.CAMPAIGN,
        title: 'Run social ads',
        prompt: 'Run social ads for my music',
        description: 'Launch ad campaigns on social media.',
        priority: 'high',
        estimatedTime: '45 minutes',
        relatedContent: null
      },
      {
        id: '4',
        type: TaskType.MARKETING_PLAN,
        title: 'Make a marketing plan',
        prompt: 'Make a marketing plan for my music',
        description: 'Get a comprehensive marketing strategy.',
        priority: 'high',
        estimatedTime: '1 hour',
        relatedContent: null
      },
      {
        id: '5',
        type: TaskType.CONTENT_ANALYSIS,
        title: 'Build a content strategy',
        prompt: 'Build a content strategy for my social media',
        description: 'Develop a plan for your social media content.',
        priority: 'medium',
        estimatedTime: '45 minutes',
        relatedContent: null
      },
      {
        id: '6',
        type: TaskType.CONTENT_ANALYSIS,
        title: 'Analyze my posts',
        prompt: 'Analyze my social media posts',
        description: 'Get insights on your top-performing content.',
        priority: 'medium',
        estimatedTime: '20 minutes',
        relatedContent: null
      }
    ]
  }



  /** Get priority ranking for sorting (high = 3, medium = 2, low = 1) */
  const getPriorityRank = (priority: 'high' | 'medium' | 'low'): number => {
    switch (priority) {
      case 'high': return 3;
      case 'medium': return 2;
      case 'low': return 1;
      default: return 0;
    }
  };

  /** Sort tasks by priority in descending order and take top 5-6 for desktop, all for mobile */
  const sortedTasks = [...tasksToShow]
    .sort((a, b) => getPriorityRank(b.priority) - getPriorityRank(a.priority))
    .slice(0, mediumView ? tasksToShow.length : 6);

  /** Get a colorful gradient and icon for each task */
  const getTaskStyle = (index: number) => {

    let gradients = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4e54c8 0%, #8f94fb 100%)', // indigo suite
      'linear-gradient(135deg, #7928ca 0%, #ff0080 100%)', // purple → magenta
      'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)', // cyan → azure
      'linear-gradient(135deg, #ff512f 0%, #dd2476 100%)', // sunset punch
      'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', // royal violet → electric blue
      'linear-gradient(135deg, #00b09b 0%, #96c93d 100%)', // teal → lime

    ];

    const icons = ['lightening', 'star', 'wand', 'heart', 'music', 'boost', 'target', 'upload'];

    return {
      background: gradients[index % gradients.length],
      icon: icons[index % icons.length]
    };
  };

  const handleTaskClick = (task: AgentTaskOptions) => {
    // Create a task with the prompt as the title for the chat
    const taskForChat: AgentTaskOptions = {
      ...task,
      title: task.prompt || task.title
    };
    onTaskClick(taskForChat);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: (i: number) => ({
      opacity: 1,
      transition: {
        delay: i * 0.05, // Even faster delay since we have more items
      },
    }),
  };

  const renderTasks = () => {
    if (isLoadingRecommendedTasks) {
      return <div><Loader /></div>
    }

    return sortedTasks.map((task, index) => {
      const taskStyle = getTaskStyle(index);

      return (
        <motion.button
          key={task.id}
          className="taskCard"
          style={styx(
            sty.taskCard,
            { background: taskStyle.background },
            mediumView && sty.taskCardMobile
          )}
          onClick={() => handleTaskClick(task)}
          custom={index}
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          whileHover={{
            y: -8,
            scale: 1.02,
            boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
            transition: { duration: 0.3 }
          }}
          whileTap={{
            y: -4,
            scale: 0.98,
            transition: { duration: 0.1 }
          }}
        >
          <div className="taskIconContainer" style={sty.taskIconContainer}>
            <Icon
              className="taskIcon"
              icon={taskStyle.icon}
              size={24}
              color="white"
            />
          </div>

          <div className="taskContent" style={sty.taskContent}>
            <h4 className="taskTitle" style={sty.taskTitle}>
              {task.title}
            </h4>
          </div>

          <div className="taskArrow" style={sty.taskArrow}>
            <Icon
              className="arrowIcon"
              icon="arrowRight"
              size={20}
              color="white"
            />
          </div>
        </motion.button>
      );
    })
  }


  return (
    <div className="dailyTasksContainer" style={sty.dailyTasksContainer}>
      <div
        className="tasksGrid"
        style={styx(
          mStyle(sty, 'tasksGrid'),
          mediumView && sty.tasksGridMobile
        )}
      >
        {renderTasks()}
      </div>
    </div>
  );
};

const sty: Record<string, CSSProperties> = {
  dailyTasksContainer: {
    width: '100%',
  },
  onboardingCopy: {
    marginBottom: 24,
    fontSize: 18,
    color: Colors.greyDark,
    textAlign: 'left',
    fontWeight: 600,
  },
  tasksGrid: {
    display: 'flex',
    flexDirection: 'row',
    gap: 16,
    width: '100%',
    flexWrap: 'wrap',
  },
  tasksGridMobile: {
    flexDirection: 'column',
    gap: 12,
    flexWrap: 'nowrap',
  },
  taskCard: {
    border: 'none',
    borderRadius: 12,
    padding: 16,
    cursor: 'pointer',
    transition: 'all 0.3s ease-in-out',
    boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // minHeight: 80,
    flex: '1 1 calc(33.333% - 12px)',
    textAlign: 'left',
    position: 'relative',
    overflow: 'hidden',
    color: 'white',
  },
  taskCardMobile: {
    minHeight: 70,
    flex: 'none',
    padding: 14,
  },
  taskIconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    marginRight: 12,
    flexShrink: 0,
  },
  taskContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flex: 1,
  },
  taskTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 500,
    color: 'white',
    lineHeight: 1.2,
    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  taskArrow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    marginLeft: 12,
    flexShrink: 0,
    transition: 'transform 0.2s ease-in-out',
  },
};

export default DailyTasks;
